import { Box, IconButton, Stack, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { Field } from 'formik';
import { CustomMaterialSchema, queryLinksForCustomMaterial } from '../schema';
import DeleteCustomMaterialDialog from './DeleteCustomMaterialDialog';
import { useCallback, useState } from 'react';
import FormTextInput from './FormTextInput';
import CustomMaterialCategoryDropdown from './CustomMaterialCategoryDropdown';
import { getOneLink } from 'src/utils';

interface Props {
    index: number;
    eventName: string;
    customMaterial: CustomMaterialSchema;
    editMode: boolean;
    remove: (index: number) => void;
}

export default function CustomMaterialListSection(props: Props) {
    const { index, eventName, customMaterial, editMode, remove } = props;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState<CustomMaterialSchema | undefined>(undefined);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
    }, [setDialogOpen]);

    const removeField = useCallback(() => {
        remove(index);
    }, [index, remove]);

    // whether to just remove the material
    // (because it's not in the database,
    // so don't need to delete)
    const justRemove = customMaterial.id === undefined;
    const handleClickRemove = useCallback(() => {
        remove(index);
    }, [index, remove]);

    const handleClickDelete = useCallback(() => {
        setMaterialToDelete(customMaterial);
        setDialogOpen(true);
    }, [customMaterial, setMaterialToDelete, setDialogOpen]);

    const { data, error } = queryLinksForCustomMaterial(props.customMaterial?.id);
    const numMeasurements = data?.wasteMeasurements?.length || 0;

    const category = getOneLink(data?.wasteCategories);

    if (error) {
        console.error(error);
        return <></>;
    }

    return (
        <Box>
            <DeleteCustomMaterialDialog
                eventName={eventName}
                customMaterial={materialToDelete}
                open={dialogOpen}
                onClose={handleCloseDialog}
                numMeasurements={numMeasurements}
                removeField={removeField}
            />
            <Stack direction="column" spacing={1} sx={{ position: 'relative' }}>
                <Box sx={{ position: 'relative' }}>
                    {editMode ? (
                        <Stack direction="column" spacing={1} sx={{ alignItems: 'flex-start' }}>
                            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
                                <Field
                                    name={`customMaterials[${index}].name`}
                                    component={FormTextInput}
                                    required={true}
                                    label="Material name"
                                    sx={{ flexGrow: 1 }}
                                />
                                <CustomMaterialCategoryDropdown index={index} />
                                {justRemove ? (
                                    <IconButton sx={{}} onClick={handleClickRemove}>
                                        <CloseIcon />
                                    </IconButton>
                                ) : (
                                    <IconButton sx={{}} onClick={handleClickDelete}>
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                                <Field
                                    name={`customMaterials[${index}].lbsPerYd3`}
                                    component={FormTextInput}
                                    label="Density (lbs/yd³)"
                                    type="number"
                                    sx={{ width: '150px' }}
                                    inputProps={{
                                        inputMode: 'decimal',
                                        step: '0.1',
                                    }}
                                />
                                <Field
                                    name={`customMaterials[${index}].mtCo2eSavedPerTon`}
                                    component={FormTextInput}
                                    label="MTCO2E saved per ton"
                                    type="number"
                                    sx={{ width: '150px' }}
                                    inputProps={{
                                        inputMode: 'decimal',
                                        step: '0.01',
                                    }}
                                />
                            </Stack>
                        </Stack>
                    ) : (
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                #{index + 1}: {customMaterial.name}
                            </Typography>
                            <Stack spacing={0.5} sx={{ mt: 1, pl: 2 }}>
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                    • Category: {category?.name || 'None'}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                    • Measurements: {numMeasurements}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                    • Density: {customMaterial.lbsPerYd3 ? `${customMaterial.lbsPerYd3} lbs/yd³` : 'None'}
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                    • MTCO2E saved per ton: {customMaterial.mtCo2eSavedPerTon ? customMaterial.mtCo2eSavedPerTon : 'None'}
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Box>
                <br />
            </Stack>
        </Box>
    );
}
